import { Button, LinkButton } from './primitives/Button.js';
import { Container, Eyebrow } from './primitives/Misc.js';
import { useAnim3d } from '../hooks/useAnim3d.js';
import './Hero.css';

export interface HeroProps {
  onOpenAudit: () => void;
  onWatchVideo: () => void;
}

interface Stage {
  index: string;
  stage: string;
  caption: string;
  /** The step we are best known for, highlighted so it reads as one of six. */
  highlight?: boolean;
}

/**
 * The readiness pipeline. Its whole job is to say the thing the old
 * dedupe-first hero could not: this is a process, and deduplication is one
 * step inside it. Hence step 03 is highlighted rather than hidden — being
 * good at the hard step is the proof, not the pitch.
 */
const STAGES: Stage[] = [
  { index: '01', stage: 'connect', caption: 'every system holding customer data, read-only' },
  { index: '02', stage: 'profile', caption: 'every field measured — completeness, format, PII' },
  { index: '03', stage: 'deduplicate', caption: 'one record per customer, evidence shown', highlight: true },
  { index: '04', stage: 'normalise', caption: 'titles, countries, phone formats to one standard' },
  { index: '05', stage: 'reconcile', caption: 'what exists here and is missing there' },
  { index: '06', stage: 'deliver', caption: 'clean records to your lake, warehouse or AI' },
];

function ReadinessPipeline() {
  return (
    <div className="land-pipe-frame" aria-hidden="true">
      <div className="land-pipe">
        <span className="land-pipe__rail" />
        {STAGES.map((item, position) => (
          <div
            key={item.index}
            className={`land-pipe__row${item.highlight ? ' land-pipe__row--on' : ''}`}
            style={{ transform: `translateZ(${position * 7}px)` }}
          >
            <span className="land-pipe__index">{item.index}</span>
            <span className="land-pipe__body">
              <span className="land-pipe__stage">{item.stage}</span>
              <span className="land-pipe__caption">{item.caption}</span>
            </span>
          </div>
        ))}
        <div className="land-pipe__loop">
          <span className="land-pipe__loop-glyph" data-anim3d="1">
            ↻
          </span>
          then keep it fit — back to 02, continuously
        </div>
      </div>
    </div>
  );
}

export function Hero({ onOpenAudit, onWatchVideo }: HeroProps) {
  useAnim3d();

  return (
    <Container className="land-hero">
      <Eyebrow>Data readiness for AI · read-only to start</Eyebrow>
      <h1 className="land-hero__headline">Get your data AI-ready. Then keep it that way.</h1>
      <div className="land-hero__split">
        <div>
          <p className="land-hero__lede">
            Dedupr connects every system your business keeps customer data in, profiles what it
            finds, then deduplicates, normalises and reconciles it — so what reaches your warehouse,
            your reports and your AI is one trustworthy version of each customer. Run it once as
            preparation, or leave it running as the gate that keeps data AI-fit. Nothing changes
            until you approve it, every change is snapshotted, and any run is reversible for 30 days.
          </p>
          <div className="land-hero__ctas">
            <Button variant="primary" size="lg" onClick={onOpenAudit}>
              Run a free data audit
            </Button>
            <Button variant="secondary" size="lg" onClick={onWatchVideo}>
              <span className="land-hero__play" aria-hidden="true">
                ▶
              </span>
              Watch the review
            </Button>
          </div>
          <LinkButton variant="plain" href="#modes" className="land-hero__tertiary">
            Or try it on your own data first, in your browser →
          </LinkButton>
          <div className="land-hero__stats">
            <div>
              <div className="land-hero__stat-value">~65 tools</div>
              <div className="land-hero__stat-label">CRM, marketing, finance, support, docs</div>
            </div>
            <div>
              <div className="land-hero__stat-value">30 days</div>
              <div className="land-hero__stat-label">every approved run stays reversible</div>
            </div>
            <div>
              <div className="land-hero__stat-value">0</div>
              <div className="land-hero__stat-label">records deleted by default</div>
            </div>
          </div>
        </div>
        <ReadinessPipeline />
      </div>
    </Container>
  );
}
